import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Volume2, Play, Square, Upload, Trash2, PhoneCall, MessageSquare, AlertTriangle, Loader2, Users, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Slider } from '../ui/slider';
import { toast } from 'sonner';
import {
  SMS_TONES, RING_TONES, previewTone, stopPreview, playSmsTone,
  getAudioBlockReason, updateSoundPrefs,
} from '../../utils/notificationSound';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('token') || localStorage.getItem('tech_token');
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

const PreviewBtn = ({ playing, onClick, testId }) => (
  <Button size="sm" variant="outline" onClick={onClick} className="h-9 px-3 shrink-0" data-testid={testId}>
    {playing ? <Square className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
  </Button>
);

const ToneSelect = ({ kind, value, onChange, hasCustom, testId, disabled }) => {
  const tones = (kind === 'sms' ? SMS_TONES : RING_TONES).filter((t) => t.id !== 'custom' || hasCustom);
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="w-full" data-testid={testId}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {tones.map((t) => (
          <SelectItem key={t.id} value={t.id} data-testid={`${testId}-opt-${t.id}`}>{t.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default function SoundsAlertsCard({ preferences, onPrefChange, saving }) {
  const [defaults, setDefaults] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [playing, setPlaying] = useState(null);
  const [blockReason, setBlockReason] = useState(null);
  const smsFileRef = useRef(null);
  const ringFileRef = useRef(null);

  const fetchDefaults = useCallback(async () => {
    try {
      const res = await fetch(`${API}/push/sound-defaults`, { headers: authHeaders() });
      if (res.ok) {
        const d = await res.json();
        setDefaults(d);
        updateSoundPrefs({
          account_default_sms_tone: d.default_sms_tone || 'default',
          account_default_ringtone: d.default_call_ringtone || 'default',
          location_ringtones: d.location_ringtones || {},
        });
      }
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => {
    fetchDefaults();
    return () => stopPreview();
  }, [fetchDefaults]);

  const doPreview = (kind, id) => {
    const key = `${kind}:${id}`;
    if (playing === key) {
      stopPreview();
      setPlaying(null);
      return;
    }
    const audio = previewTone(kind, id, kind === 'ring' ? { volume: preferences.ring_volume ?? 1 } : {});
    setPlaying(key);
    setBlockReason(getAudioBlockReason());
    if (audio) {
      audio.onended = () => setPlaying((p) => (p === key ? null : p));
    } else {
      setTimeout(() => setPlaying((p) => (p === key ? null : p)), 800);
    }
  };

  const handleTestSms = () => {
    const reason = getAudioBlockReason();
    setBlockReason(reason);
    if (preferences.sms_tone === 'silent') {
      toast.info('SMS tone is set to Silent — no sound will play for incoming messages');
      return;
    }
    if (preferences.notification_sound === false) {
      toast.info('SMS sound is turned off — enable it to hear incoming message tones');
      return;
    }
    playSmsTone({ force: true, toneId: preferences.sms_tone || 'default' });
    toast.success(reason ? 'Tried to play — see the warning below' : 'If you heard the tone, SMS sound is working');
  };

  const handleTestRing = () => {
    const reason = getAudioBlockReason();
    setBlockReason(reason);
    if (preferences.call_ring_enabled === false || preferences.call_ringtone === 'silent') {
      toast.warning('Your call ringtone is silent/disabled — you may miss incoming calls on this device');
      return;
    }
    doPreview('ring', preferences.call_ringtone || 'default');
    toast.success(reason ? 'Tried to play — see the warning below' : 'Playing one ring cycle of your selected ringtone');
  };

  const handleUpload = async (kind, file) => {
    if (!file) return;
    if (!/\.(mp3|wav|m4a)$/i.test(file.name)) {
      toast.error('Only MP3, WAV or M4A files are supported');
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error('File too large — maximum 3 MB');
      return;
    }
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', kind);
      const res = await fetch(`${API}/push/custom-sound`, { method: 'POST', headers: authHeaders(), body: fd });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const urlKey = kind === 'sms' ? 'custom_sms_url' : 'custom_ring_url';
        const toneKey = kind === 'sms' ? 'sms_tone' : 'call_ringtone';
        updateSoundPrefs({ [urlKey]: data.url });
        onPrefChange({ [urlKey]: data.url, [toneKey]: 'custom' });
        toast.success('Custom sound uploaded and selected');
      } else {
        toast.error(data.detail || 'Upload failed — the file may be invalid or too long');
      }
    } catch {
      toast.error('Upload failed — please try again');
    } finally {
      setUploading(null);
      if (smsFileRef.current) smsFileRef.current.value = '';
      if (ringFileRef.current) ringFileRef.current.value = '';
    }
  };

  const handleDeleteCustom = async (kind) => {
    try {
      const res = await fetch(`${API}/push/custom-sound/${kind}`, { method: 'DELETE', headers: authHeaders() });
      if (res.ok) {
        const urlKey = kind === 'sms' ? 'custom_sms_url' : 'custom_ring_url';
        const toneKey = kind === 'sms' ? 'sms_tone' : 'call_ringtone';
        const partial = { [urlKey]: null };
        if (preferences[toneKey] === 'custom') partial[toneKey] = 'default';
        updateSoundPrefs({ [urlKey]: null });
        onPrefChange(partial);
        toast.success('Custom sound removed');
      }
    } catch {
      toast.error('Could not remove the custom sound');
    }
  };

  const saveDefaults = async (partial) => {
    try {
      const res = await fetch(`${API}/push/sound-defaults`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(partial),
      });
      if (res.ok) {
        const merged = { ...defaults, ...partial };
        setDefaults(merged);
        updateSoundPrefs({
          account_default_sms_tone: merged.default_sms_tone || 'default',
          account_default_ringtone: merged.default_call_ringtone || 'default',
          location_ringtones: merged.location_ringtones || {},
        });
        toast.success('Account sound defaults saved');
      } else {
        toast.error('Failed to save defaults');
      }
    } catch {
      toast.error('Failed to save defaults');
    }
  };

  const locMap = defaults?.location_ringtones || {};
  const hasLocRingtones = Object.values(locMap).some(Boolean);
  const ringSilent = preferences.call_ring_enabled === false || preferences.call_ringtone === 'silent';

  return (
    <div className="space-y-4" data-testid="sounds-alerts-section">
      <div className="flex items-center gap-2">
        <Volume2 className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium text-sm">Sounds &amp; Alerts</span>
      </div>

      {/* ── Incoming SMS tone ── */}
      <div className="p-3 border rounded-lg space-y-3" data-testid="sms-sound-block">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-medium flex items-center gap-1.5">
              <MessageSquare className="h-3.5 w-3.5" /> Incoming SMS Tone
            </Label>
            <p className="text-xs text-muted-foreground">Plays when a new text message arrives while the app is open</p>
          </div>
          <Switch
            checked={preferences.notification_sound !== false}
            onCheckedChange={(v) => onPrefChange({ notification_sound: v })}
            disabled={saving}
            data-testid="sms-sound-switch"
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <ToneSelect
              kind="sms"
              value={preferences.sms_tone || 'default'}
              onChange={(v) => onPrefChange({ sms_tone: v })}
              hasCustom={!!preferences.custom_sms_url}
              testId="sms-tone-select"
              disabled={saving}
            />
          </div>
          <PreviewBtn
            playing={playing === `sms:${preferences.sms_tone || 'default'}`}
            onClick={() => doPreview('sms', preferences.sms_tone || 'default')}
            testId="sms-tone-preview-btn"
          />
          <Button size="sm" variant="ghost" onClick={handleTestSms} className="h-9 text-xs" data-testid="test-sms-sound-btn">
            Test SMS Sound
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={smsFileRef}
            type="file"
            accept=".mp3,.wav,.m4a,audio/mpeg,audio/wav,audio/mp4"
            className="hidden"
            onChange={(e) => handleUpload('sms', e.target.files?.[0])}
            data-testid="sms-custom-upload-input"
          />
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            disabled={uploading === 'sms'}
            onClick={() => smsFileRef.current?.click()}
            data-testid="sms-custom-upload-btn"
          >
            {uploading === 'sms' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}
            Upload custom (MP3/WAV/M4A, max 10s)
          </Button>
          {preferences.custom_sms_url && (
            <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={() => handleDeleteCustom('sms')} data-testid="sms-custom-delete-btn">
              <Trash2 className="h-3 w-3 mr-1" /> Remove custom
            </Button>
          )}
        </div>
      </div>

      {/* ── In-app call ringtone ── */}
      <div className="p-3 border rounded-lg space-y-3" data-testid="ring-sound-block">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-medium flex items-center gap-1.5">
              <PhoneCall className="h-3.5 w-3.5" /> In-App Call Ringtone
            </Label>
            <p className="text-xs text-muted-foreground">Plays while an inbound call is ringing your device</p>
          </div>
          <Switch
            checked={preferences.call_ring_enabled !== false}
            onCheckedChange={(v) => onPrefChange({ call_ring_enabled: v })}
            disabled={saving}
            data-testid="ring-sound-switch"
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <ToneSelect
              kind="ring"
              value={preferences.call_ringtone || 'default'}
              onChange={(v) => onPrefChange({ call_ringtone: v })}
              hasCustom={!!preferences.custom_ring_url}
              testId="ring-tone-select"
              disabled={saving}
            />
          </div>
          <PreviewBtn
            playing={playing === `ring:${preferences.call_ringtone || 'default'}`}
            onClick={() => doPreview('ring', preferences.call_ringtone || 'default')}
            testId="ring-tone-preview-btn"
          />
          <Button size="sm" variant="ghost" onClick={handleTestRing} className="h-9 text-xs" data-testid="test-call-ringtone-btn">
            Test Call Ringtone
          </Button>
        </div>
        {ringSilent && (
          <div className="flex items-start gap-2 p-2 rounded-md bg-amber-500/10 border border-amber-500/30" data-testid="ring-silent-warning">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Your call ringtone is silent — incoming in-app calls will only show on screen and you may miss them.
            </p>
          </div>
        )}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">Ringtone volume</Label>
            <span className="text-xs text-muted-foreground">{Math.round((preferences.ring_volume ?? 1) * 100)}%</span>
          </div>
          <Slider
            value={[Math.round((preferences.ring_volume ?? 1) * 100)]}
            min={10}
            max={100}
            step={5}
            onValueCommit={(v) => onPrefChange({ ring_volume: v[0] / 100 })}
            data-testid="ring-volume-slider"
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={ringFileRef}
            type="file"
            accept=".mp3,.wav,.m4a,audio/mpeg,audio/wav,audio/mp4"
            className="hidden"
            onChange={(e) => handleUpload('ring', e.target.files?.[0])}
            data-testid="ring-custom-upload-input"
          />
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            disabled={uploading === 'ring'}
            onClick={() => ringFileRef.current?.click()}
            data-testid="ring-custom-upload-btn"
          >
            {uploading === 'ring' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}
            Upload custom (MP3/WAV/M4A, max 45s)
          </Button>
          {preferences.custom_ring_url && (
            <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={() => handleDeleteCustom('ring')} data-testid="ring-custom-delete-btn">
              <Trash2 className="h-3 w-3 mr-1" /> Remove custom
            </Button>
          )}
        </div>
      </div>

      {blockReason && (
        <div className="flex items-start gap-2 p-2 rounded-md bg-amber-500/10 border border-amber-500/30" data-testid="audio-block-reason">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-600 dark:text-amber-400">{blockReason}</p>
        </div>
      )}

      {/* ── Location-specific ringtones (multi-location) ── */}
      {defaults?.locations?.length > 0 && (
        <div className="p-3 border rounded-lg space-y-3" data-testid="location-ringtones-block">
          <div>
            <Label className="text-sm font-medium flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" /> Location Ringtones
            </Label>
            <p className="text-xs text-muted-foreground">
              Hear a different ringtone depending on which location the call is for. Location ringtones override personal choices when enabled.
            </p>
          </div>
          {hasLocRingtones && (
            <div className="flex items-center justify-between">
              <Label className="text-xs">Use location ringtones on this account</Label>
              <Switch
                checked={preferences.location_ringtones_enabled !== false}
                onCheckedChange={(v) => onPrefChange({ location_ringtones_enabled: v })}
                disabled={saving}
                data-testid="location-ringtones-toggle"
              />
            </div>
          )}
          {defaults.can_edit ? (
            <div className="space-y-2">
              {defaults.locations.map((loc) => {
                const cur = locMap[loc.location_id] || 'none';
                return (
                  <div key={loc.location_id} className="flex items-center gap-2">
                    <span className="text-xs w-32 truncate shrink-0" title={loc.name}>{loc.name}</span>
                    <div className="flex-1">
                      <Select
                        value={cur}
                        onValueChange={(v) => {
                          const next = { ...locMap };
                          if (v === 'none') delete next[loc.location_id];
                          else next[loc.location_id] = v;
                          saveDefaults({ location_ringtones: next });
                        }}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid={`loc-ring-select-${loc.location_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No override (use personal ringtone)</SelectItem>
                          {RING_TONES.filter((t) => t.id !== 'custom' && t.id !== 'silent').map((t) => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {cur !== 'none' && (
                      <PreviewBtn
                        playing={playing === `ring:${cur}`}
                        onClick={() => doPreview('ring', cur)}
                        testId={`loc-ring-preview-${loc.location_id}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            hasLocRingtones && (
              <p className="text-xs text-muted-foreground">Location ringtones are managed by your account admin.</p>
            )
          )}
        </div>
      )}

      {/* ── Account defaults for new users (owner/admin) ── */}
      {defaults?.can_edit && (
        <div className="p-3 border rounded-lg space-y-3" data-testid="team-sound-defaults-block">
          <div>
            <Label className="text-sm font-medium flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" /> Team Default Sounds
            </Label>
            <p className="text-xs text-muted-foreground">
              Used for new team members and anyone who hasn't picked their own tone. Changing this never overrides a user's personal choice.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Default SMS tone</Label>
              <Select
                value={defaults.default_sms_tone || 'default'}
                onValueChange={(v) => saveDefaults({ default_sms_tone: v })}
              >
                <SelectTrigger className="h-8 text-xs" data-testid="default-sms-tone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SMS_TONES.filter((t) => t.id !== 'custom').map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Default call ringtone</Label>
              <Select
                value={defaults.default_call_ringtone || 'default'}
                onValueChange={(v) => saveDefaults({ default_call_ringtone: v })}
              >
                <SelectTrigger className="h-8 text-xs" data-testid="default-ring-tone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RING_TONES.filter((t) => t.id !== 'custom' && t.id !== 'silent').map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground leading-relaxed">
        Custom tones play while Fieldz AI is open (browser tab, installed PWA, or the mobile app in the foreground).
        Background and lock-screen push notifications use your device's system notification sound — browsers and
        operating systems don't allow websites to change it. Device mute, Do&nbsp;Not&nbsp;Disturb and Focus modes are always respected.
      </p>
    </div>
  );
}
